/* eslint-disable jsdoc/require-param-description */
/* eslint-disable jsdoc/require-returns */
import { useQuery } from "@tanstack/react-query";
import { getDoc, QuerySnapshot, QueryDocumentSnapshot } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import {
  getAllColaboradoresAtivosENaoExcluidos,
  getColaboradorDocumentReferenceFromPath
} from "@/modules/colaboradores/firestore";
import { getComentariosWithDateFilter } from "@/modules/desafio-comentarios/firestore";
import { getCronogramaDesafioFilter } from "@/modules/desafio-cronograma/firestore";
import { getDesafiosPublishedWithDateFilter } from "@/modules/desafios/firestore";
import useUserStore from "@/modules/users/store";
import { getTotalMoedasForDesafioAggregation } from "@/src/modules/desafios-activities-per-colaborador/firestore";
import { FiltroDesafio } from "@/src/modules/desafios-relatorio-visao-geral/components/ModalFilterDesafios";
import { Cards } from "@/src/routes/desafios-relatorio-visao-geral/DesafiosRelatorioVisaoGeralPage";

// Constantes para percentuais e datas
const CEM_PORCENTO = 100;
const ANO = 5000;
const MENOR_DATA = new Date(ANO, 0, 1);
const MAIOR_DATA = new Date(ANO * -1, 0, 1);

// Constantes para cache e timeouts
const CACHE_STALE_TIME_MINUTES = 5;
const MINUTES_TO_MILLISECONDS = 60;
const SECONDS_TO_MILLISECONDS = 1000;

// Estrutura de dados enriquecida para a tabela
export interface TDesafioProcessado extends TDesafio {
  propositorNome: string;
  propositorEmail: string;
  moedas: number;
  primeiraImersao?: Date | null;
  ultimaImersao?: Date | null;
}

const defaultCards: Cards = {
  colaboradoresAtivos: 0,
  desafios: { total: 0, participantes: new Set(), porcentagem: 0 },
  comentarios: { total: 0, participantes: new Set(), porcentagem: 0 }
};

// Cache simples em memória para colaboradores
const colaboradorCache = new Map<string, TColaborador>();

/**
 * Interface para dados base necessários para processamento
 */
interface BaseData {
  colaboradoresAtivos: TColaborador[];
  desafios: TDesafio[];
  todosDesafios: TDesafio[];
}

/**
 * Interface para dados agregados de comentários, moedas e cronogramas
 */
interface AggregatedData {
  comentariosSnaps: QuerySnapshot[];
  moedasResult: number[];
  cronogramasSnaps: QuerySnapshot[];
}

/**
 * Busca um colaborador por path, utilizando cache em memória para otimização
 * @param path - Caminho de referência do colaborador no Firestore
 * @returns Promise que resolve para o colaborador encontrado ou null se não existir
 */
async function getColaborador(path: string): Promise<TColaborador | null> {
  if (colaboradorCache.has(path)) {
    const cachedColaborador = colaboradorCache.get(path);
    if (cachedColaborador) {
      return cachedColaborador;
    }
  }
  try {
    const documentReference = getColaboradorDocumentReferenceFromPath(path);
    const snapshot = await getDoc(documentReference);
    if (snapshot.exists()) {
      const data = snapshot.data() as TColaborador;
      colaboradorCache.set(path, data);
      return data;
    }
  } catch (error) {
    console.error("Falha ao buscar colaborador:", error);
  }
  return null;
}

/**
 * Busca dados base de colaboradores e desafios
 * @param empresaId - ID da empresa
 * @param dateRange - Intervalo de datas
 * @param dateRange.dataInicial
 * @param dateRange.dataFinal
 * @param customFilters - Filtros customizados
 * @returns Promise com dados base organizados
 */
async function fetchBaseData(
  empresaId: string,
  dateRange: { dataInicial: Date; dataFinal: Date },
  customFilters: FiltroDesafio
): Promise<BaseData> {
  // eslint-disable-next-line compat/compat
  const [colaboradoresSnap, desafiosSnap, todosDesafiosSnap] = await Promise.all([
    getAllColaboradoresAtivosENaoExcluidos(empresaId),
    getDesafiosPublishedWithDateFilter(
      empresaId,
      dateRange.dataInicial,
      dateRange.dataFinal,
      (customFilters.status as TDesafioStatus) || undefined
    ),
    // Buscar todos os desafios para comentários independentes
    getDesafiosPublishedWithDateFilter(empresaId, new Date(0), new Date(), undefined)
  ]);

  return {
    colaboradoresAtivos: colaboradoresSnap.docs.map((document_) => document_.data()),
    desafios: desafiosSnap.docs.map((document_) => document_.data()),
    todosDesafios: todosDesafiosSnap.docs.map((document_) => document_.data())
  };
}

/**
 * Busca dados agregados de comentários, moedas e cronogramas
 * @param empresaId - ID da empresa
 * @param baseData - Dados base já carregados
 * @param dateRange - Intervalo de datas
 * @param dateRange.dataInicial
 * @param dateRange.dataFinal
 * @returns Promise com dados agregados
 */
async function fetchAggregatedData(
  empresaId: string,
  baseData: BaseData,
  dateRange: { dataInicial: Date; dataFinal: Date }
): Promise<AggregatedData> {
  const comentariosPromises = baseData.todosDesafios.map((d) =>
    getComentariosWithDateFilter(empresaId, d.id, dateRange.dataInicial, dateRange.dataFinal)
  );
  const moedasPromises = baseData.desafios.map((d) => getTotalMoedasForDesafioAggregation(empresaId, d.id));
  const cronogramasPromises = baseData.desafios.map((d) => getCronogramaDesafioFilter(empresaId, d.id));

  // eslint-disable-next-line compat/compat
  const [comentariosSnaps, moedasResult, cronogramasSnaps] = await Promise.all([
    // eslint-disable-next-line compat/compat
    Promise.all(comentariosPromises),
    // eslint-disable-next-line compat/compat
    Promise.all(moedasPromises),
    // eslint-disable-next-line compat/compat
    Promise.all(cronogramasPromises)
  ]);

  return { comentariosSnaps, moedasResult, cronogramasSnaps };
}

/**
 * Calcula estatísticas para os cards do dashboard
 * @param baseData - Dados base dos colaboradores e desafios
 * @param aggregatedData - Dados agregados de comentários
 * @returns Objeto com estatísticas dos cards
 */
function calculateCards(baseData: BaseData, aggregatedData: AggregatedData): Cards {
  const cards = { ...defaultCards, colaboradoresAtivos: baseData.colaboradoresAtivos.length };

  // Estatísticas de desafios
  const desafioParticipantes = new Set(baseData.desafios.map((d) => d.createdBy));
  cards.desafios = {
    total: baseData.desafios.length,
    participantes: desafioParticipantes,
    porcentagem:
      baseData.colaboradoresAtivos.length > 0
        ? (desafioParticipantes.size / baseData.colaboradoresAtivos.length) * CEM_PORCENTO
        : 0
  };

  // Estatísticas de comentários
  const comentarioParticipantes = new Set<string>();
  for (const snap of aggregatedData.comentariosSnaps) {
    for (const document_ of snap.docs) {
      const documentData = document_.data() as { createdBy: string };
      comentarioParticipantes.add(documentData.createdBy);
    }
  }

  cards.comentarios = {
    total: aggregatedData.comentariosSnaps.reduce((accumulator, snap) => accumulator + snap.docs.length, 0),
    participantes: comentarioParticipantes,
    porcentagem:
      baseData.colaboradoresAtivos.length > 0
        ? (comentarioParticipantes.size / baseData.colaboradoresAtivos.length) * CEM_PORCENTO
        : 0
  };

  return cards;
}

/**
 * Calcula datas de primeira e última imersão para um desafio
 * @param cronogramas - Array de documentos de cronograma do desafio
 * @returns Objeto com primeira e última imersão
 */
function calculateImersionDates(cronogramas: QueryDocumentSnapshot[]): {
  primeiraImersao: Date | null;
  ultimaImersao: Date | null;
} {
  let menorData = MENOR_DATA;
  let maiorData = MAIOR_DATA;

  for (const cronoDocument of cronogramas) {
    const cronoData = cronoDocument.data() as { event: Array<{ data: { toDate(): Date } }> };
    for (const evento of cronoData.event) {
      const eventoTimestamp = evento.data.toDate().getTime();
      if (eventoTimestamp > maiorData.getTime()) maiorData = new Date(eventoTimestamp);
      if (eventoTimestamp < menorData.getTime()) menorData = new Date(eventoTimestamp);
    }
  }

  return {
    primeiraImersao: menorData === MENOR_DATA ? null : menorData,
    ultimaImersao: maiorData === MAIOR_DATA ? null : maiorData
  };
}

/**
 * Processa desafios adicionando dados enriquecidos
 * @param baseData - Dados base de colaboradores e desafios
 * @param aggregatedData - Dados agregados
 * @returns Promise com lista de desafios processados
 */
async function processDesafios(baseData: BaseData, aggregatedData: AggregatedData): Promise<TDesafioProcessado[]> {
  const colaboradoresMap = new Map(baseData.colaboradoresAtivos.map((c) => [c.id, c]));
  const moedasPorDesafio = new Map<string, number>();
  const cronogramasPorDesafio = new Map<string, QueryDocumentSnapshot[]>();

  for (let index = 0; index < baseData.desafios.length; index++) {
    const desafio = baseData.desafios[index] ?? null;
    if (desafio && index < aggregatedData.moedasResult.length) {
      moedasPorDesafio.set(desafio.id, aggregatedData.moedasResult[index] ?? 0);
    }
    if (desafio && index < aggregatedData.cronogramasSnaps.length) {
      cronogramasPorDesafio.set(desafio.id, aggregatedData.cronogramasSnaps[index]?.docs ?? []);
    }
  }

  const desafiosProcessadosPromises = baseData.desafios.map(async (desafio) => {
    const propositor =
      colaboradoresMap.get(desafio.createdBy) ?? (await getColaborador(desafio.propositorReference as string));

    const cronogramas = cronogramasPorDesafio.get(desafio.id) || [];
    const { primeiraImersao, ultimaImersao } = calculateImersionDates(cronogramas);

    return {
      ...desafio,
      propositorNome: propositor?.nome ?? "Não encontrado",
      propositorEmail: propositor?.email ?? "Não encontrado",
      moedas: moedasPorDesafio.get(desafio.id) ?? 0,
      primeiraImersao,
      ultimaImersao
    };
  });

  // eslint-disable-next-line compat/compat
  return Promise.all(desafiosProcessadosPromises);
}

/**
 * Hook centralizador para buscar e processar todos os dados do relatório de desafios.
 * Integra dados de desafios, colaboradores, comentários e cronogramas para gerar
 * estatísticas e relatórios completos.
 * @param dateRange - O intervalo de datas para o filtro principal
 * @param dateRange.dataInicial - Data inicial do período de análise
 * @param dateRange.dataFinal - Data final do período de análise
 * @param customFilters - Os filtros customizados (status, texto de busca)
 * @param customFilters.status - Status dos desafios para filtrar
 * @param customFilters.search - Termo de busca para filtrar desafios
 * @param customFilters.campo - Campo específico para aplicar a busca
 * @returns Objeto contendo cards com estatísticas, lista de desafios processados e estado de carregamento
 * @returns cards - Dados estatísticos dos cards (colaboradores ativos, desafios, comentários)
 * @returns desafiosProcessados - Lista de desafios enriquecidos com dados adicionais
 * @returns isLoading - Estado de carregamento das operações assíncronas
 */
export function useDesafiosRelatorio(dateRange: { dataInicial: Date; dataFinal: Date }, customFilters: FiltroDesafio) {
  const empresa = useUserStore((state) => state.activeEmpresa);
  const [isProcessing, setIsProcessing] = useState(true);

  const queryKey = [
    "desafiosRelatorio",
    empresa?.id,
    dateRange.dataInicial.toISOString(),
    dateRange.dataFinal.toISOString(),
    customFilters.status,
    customFilters.search,
    customFilters.campo
  ];

  const { data, isLoading: isLoadingQuery } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- All relevant object properties are included in queryKey
    queryKey,
    queryFn: async () => {
      if (!empresa?.id) {
        throw new Error("ID da empresa não encontrado.");
      }
      setIsProcessing(true);

      // Buscar dados base
      const baseData = await fetchBaseData(empresa?.id ?? "", dateRange, customFilters);

      // Buscar dados agregados
      const aggregatedData = await fetchAggregatedData(empresa?.id ?? "", baseData, dateRange);

      // Calcular estatísticas dos cards
      const cards = calculateCards(baseData, aggregatedData);

      // Processar desafios com dados enriquecidos
      const desafiosProcessados = await processDesafios(baseData, aggregatedData);

      return { cards, desafiosProcessados };
    },
    enabled: !!empresa?.id,
    staleTime: CACHE_STALE_TIME_MINUTES * MINUTES_TO_MILLISECONDS * SECONDS_TO_MILLISECONDS
  });

  const dadosFiltrados = useMemo(() => {
    if (!data?.desafiosProcessados) return [];
    if (!customFilters.search || !customFilters.campo) {
      return data.desafiosProcessados;
    }

    const searchTerm = customFilters.search.toLowerCase();
    return data.desafiosProcessados.filter((item) => {
      switch (customFilters.campo) {
        case "Título": {
          return item.titulo.toLowerCase().includes(searchTerm);
        }
        case "Autor(es)": {
          return item.propositorNome.toLowerCase().includes(searchTerm);
        }
        case "E-mail": {
          return item.propositorEmail.toLowerCase().includes(searchTerm);
        }
        default: {
          return true;
        }
      }
    });
  }, [data?.desafiosProcessados, customFilters.search, customFilters.campo]);

  useEffect(() => {
    if (!isLoadingQuery) {
      setIsProcessing(false);
    }
  }, [isLoadingQuery]);

  return {
    cards: data?.cards ?? defaultCards,
    desafiosProcessados: dadosFiltrados,
    isLoading: isLoadingQuery || isProcessing
  };
}
